import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {PaymentProvider} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';

interface DialogData {
  id: number;
  submitCredentialsFn: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider: PaymentProvider;
}

@Component({
  selector: 'app-lytex-credentials',
  templateUrl: './lytex-credentials.component.html',
  styleUrls: ['./lytex-credentials.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
  ],
})
export class LytexCredentialsComponent {
  form: FormGroup;
  submitting = false;
  id!: number;
  submitCredentialsFn!: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider!: PaymentProvider;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.submitCredentialsFn = this.context.data.submitCredentialsFn;
    this.provider = this.context.data.provider;
    this.form = this.fb.group({
      sandbox: [false],
      apiKey: ['', [Validators.required, Validators.minLength(3)]],
      apiSecret: ['', [Validators.required, Validators.minLength(3)]],
      recipientId: [''],
    });
  }
  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;

    const payload = this.form.value as {
      sandbox?: boolean;
      apiKey: string;
      apiSecret: string;
      recipientId?: string;
    };

    let handledSuccess = false;
    const handleSuccess = () => {
      if (handledSuccess) return;
      handledSuccess = true;
      this.alerts
        .open(
          this.translate.instant(
            'features.integrations.credentials.success.message',
          ),
          {
            label: this.translate.instant(
              'features.integrations.credentials.success.title',
            ),
            appearance: 'success',
          },
        )
        .subscribe();
      this.context.completeWith(true);
    };

    this.submitCredentialsFn(this.provider, payload).subscribe({
      next: () => handleSuccess(),
      complete: () => handleSuccess(),
      error: (error: Error) => {
        console.error('Error creating API key:', error);
        this.alerts
          .open(
            this.translate.instant(
              'features.integrations.credentials.error.message',
            ),
            {
              label: this.translate.instant(
                'features.integrations.credentials.error.title',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close() {
    this.context.completeWith(false);
  }
}
